import React, { useState } from 'react';
import { FaTimes, FaMapMarkerAlt, FaRoute, FaBox, FaUser, FaPhone, FaCreditCard, FaShieldAlt, FaSignature, FaCamera, FaClock, FaDollarSign, FaTruck, FaStar, FaCalendarAlt } from 'react-icons/fa';

const JobDetailsModal = ({ isOpen, onClose, job }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (!isOpen || !job) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (date.toDate) {
        return date.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
      case 'ASSIGNED':
        return 'bg-blue-100 text-blue-800';
      case 'AVAILABLE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (progress) => {
    switch (progress) {
      case 'DELIVERED':
        return 'bg-green-100 text-green-800';
      case 'PICKED_UP':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'AVAILABLE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FaTruck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Job Details</h2>
              <p className="text-blue-100">Complete job information and tracking</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Job Header Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {job.priceFormatted || `$${job.price || 0}`}
                </div>
                <div className="text-sm text-blue-600">Total Earnings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-800 mb-2">
                  {job.route || `${job.pickupLocation} → ${job.deliveryLocation}`}
                </div>
                <div className="text-sm text-gray-600">Route</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-800 mb-2">
                  {job.category || 'Standard'}
                </div>
                <div className="text-sm text-gray-600">Category</div>
              </div>
            </div>
          </div>

          {/* Status and Progress */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <FaClock className="text-blue-500" />
                <span className="font-semibold text-gray-700">Status</span>
              </div>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(job.status)}`}>
                {job.status || 'UNKNOWN'}
              </span>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <FaRoute className="text-green-500" />
                <span className="font-semibold text-gray-700">Progress</span>
              </div>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getProgressColor(job.progress)}`}>
                {job.progress || 'AVAILABLE'}
              </span>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <FaStar className="text-yellow-500" />
                <span className="font-semibold text-gray-700">Priority</span>
              </div>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
                {job.priority || 'MEDIUM'}
              </span>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Job Details */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaBox className="mr-2 text-blue-500" />
                  Basic Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Description:</span>
                    <span className="font-medium text-gray-800">{job.description || 'No description'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Weight:</span>
                    <span className="font-medium text-gray-800">{job.weight || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dimensions:</span>
                    <span className="font-medium text-gray-800">{job.dimensions || 'N/A'}</span>
                  </div>
                  {job.specialInstructions && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Special Instructions:</span>
                      <span className="font-medium text-gray-800">{job.specialInstructions}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-green-500" />
                  Location Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <FaMapMarkerAlt className="text-red-500" />
                      <span className="font-medium text-gray-700">Pickup Location</span>
                    </div>
                    <p className="text-gray-800 bg-gray-50 p-3 rounded-lg">
                      {job.pickupLocation || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <FaMapMarkerAlt className="text-green-500" />
                      <span className="font-medium text-gray-700">Delivery Location</span>
                    </div>
                    <p className="text-gray-800 bg-gray-50 p-3 rounded-lg">
                      {job.deliveryLocation || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaUser className="mr-2 text-purple-500" />
                  Customer Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer ID:</span>
                    <span className="font-medium text-gray-800">{job.customerId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-gray-800">{job.customerName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium text-gray-800">{job.customerPhone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Additional Details */}
            <div className="space-y-6">
              {/* Payment & Insurance */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaCreditCard className="mr-2 text-green-500" />
                  Payment & Insurance
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-medium text-gray-800 capitalize">
                      {job.paymentMethod || 'Credit Card'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Insurance:</span>
                    <span className="font-medium text-gray-800">
                      {job.insurance ? (
                        <span className="text-green-600 flex items-center">
                          <FaShieldAlt className="mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-red-600">No</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Signature Required:</span>
                    <span className="font-medium text-gray-800">
                      {job.signatureRequired ? (
                        <span className="text-green-600 flex items-center">
                          <FaSignature className="mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-red-600">No</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Compensation Details */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaDollarSign className="mr-2 text-yellow-500" />
                  Compensation Details
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Pay:</span>
                    <span className="font-medium text-gray-800">
                      ${job.basePay || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Per Mile Rate:</span>
                    <span className="font-medium text-gray-800">
                      ${job.perMileRate || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Minimum Guarantee:</span>
                    <span className="font-medium text-gray-800">
                      ${job.minimumGuarantee || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Timing Information */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaClock className="mr-2 text-blue-500" />
                  Timing Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium text-gray-800">
                      {formatDate(job.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expires:</span>
                    <span className="font-medium text-gray-800">
                      {formatDate(job.expiresAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Updated:</span>
                    <span className="font-medium text-gray-800">
                      {formatDate(job.updatedAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Duration:</span>
                    <span className="font-medium text-gray-800">
                      {job.time || job.estimatedTotalTime ? `${job.estimatedTotalTime || job.time} min` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Photos Section */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaCamera className="mr-2 text-purple-500" />
              Job Photos
            </h3>
            
            {job.stagePhotos && Object.keys(job.stagePhotos).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(job.stagePhotos).map(([stage, photos]) => (
                  <div key={stage} className="border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-3 capitalize">
                      {stage.replace('_', ' ')} Photos ({photos.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {photos.map((photo, index) => (
                        <div
                          key={index}
                          className="space-y-2"
                        >
                          {/* Photo Image */}
                          <div className="relative group cursor-pointer" onClick={() => setSelectedPhoto(photo)}>
                            <img
                              src={photo.url}
                              alt={`${stage} photo ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200 group-hover:border-blue-400 transition-colors"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                              <FaCamera className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>

                          {/* ✅ LOCATION DATA DISPLAY */}
                          {photo.location && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                              <h5 className="text-xs font-semibold text-blue-800 mb-1 flex items-center">
                                📍 Location
                              </h5>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="font-medium text-blue-700">Lat:</span>
                                  <span className="text-blue-600">{photo.location.latitude?.toFixed(6) || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium text-blue-700">Lng:</span>
                                  <span className="text-blue-600">{photo.location.longitude?.toFixed(6) || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium text-blue-700">Acc:</span>
                                  <span className="text-blue-600">{photo.location.accuracy ? `${photo.location.accuracy}m` : 'N/A'}</span>
                                </div>
                                
                                {/* ✅ NEW: ADDRESS DISPLAY */}
                                {photo.address && (
                                  <div className="mt-2 pt-2 border-t border-blue-200">
                                    <div className="text-xs text-blue-700 font-medium mb-1">📍 Address:</div>
                                    <div className="text-xs text-blue-600 leading-tight">
                                      {photo.address}
                                    </div>
                                  </div>
                                )}
                                
                                {photo.location.latitude && photo.location.longitude && (
                                  <a
                                    href={`https://www.google.com/maps?q=${photo.location.latitude},${photo.location.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-center text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
                                  >
                                    🗺️ View Map
                                  </a>
                                )}
                              </div>
                            </div>
                          )}

                          {/* No Location Data Message */}
                          {!photo.location && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                              <p className="text-xs text-gray-500 text-center">
                                📍 No location data
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : job.photos && job.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {job.photos.map((photo, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedPhoto(typeof photo === 'string' ? { url: photo } : photo)}
                  >
                    <img
                      src={typeof photo === 'string' ? photo : photo.url}
                      alt={`Job photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200 group-hover:border-blue-400 transition-colors"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                      <FaCamera className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaCamera className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No photos available for this job</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <FaTimes className="h-8 w-8" />
            </button>
            <img
              src={selectedPhoto.url}
              alt="Full size photo"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailsModal;
